import moment from 'moment'
import axios from 'axios'
import store from './store'
import router from '@/router'
import { canPlanAccessModule, canPlanAccessModuleItem, getBusinessPlanKey } from '@/constants/pricingPlans'

const MAX_RETRY_TIME = 20000; // 20 seconds
const RETRY_DELAYS = [2000, 4000]; // 2s, 4s

// Cross-domain cookie for logged-in user detection (accessible by gstocky.com and all subdomains)
const shortId = (id) => {
  if (!id) return "";
  return id.length > 8 ? id.substring(0, 8) : id;
};

const setUserConnectedCookie = () => {
  document.cookie = "user_connected=true; domain=.gstocky.com; path=/; max-age=31536000; secure; samesite=lax";
};

const clearUserConnectedCookie = () => {
  document.cookie = "user_connected=; domain=.gstocky.com; path=/; max-age=0; secure; samesite=lax";
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.request.use(
  config => {
    if (config.url && config.url.includes('/admin/')) {
      const adminToken = localStorage.getItem('admin_token');
      if (adminToken) {
        config.headers['authorization'] = adminToken;
      }
      return config;
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers['authorization'] = token;
    }
    config.metadata = { startTime: new Date(), retryCount: 0, totalWait: 0 };
    return config;
  },
  error => Promise.reject(error)
);

axios.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401) {
      if (originalRequest.url && originalRequest.url.includes('/admin/')) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        router.push('/admin/login');
        return Promise.reject(error);
      }

      if (!originalRequest._retry && !originalRequest.url.includes('/refresh-token')) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['authorization'] = token;
            return axios(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          localStorage.removeItem('access_token');
          clearUserConnectedCookie();
          router.push('/login');
          return Promise.reject(error);
        }

        return new Promise((resolve, reject) => {
          axios.post(`${process.env.VUE_APP_API}/refresh-token`, { refresh_token: refreshToken })
            .then(({ data }) => {
              store.commit('SET_TOKEN', data.access_token);
              store.commit('SET_REFRESH_TOKEN', data.refresh_token);
              originalRequest.headers['authorization'] = data.access_token;
              processQueue(null, data.access_token);
              resolve(axios(originalRequest));
            })
            .catch((err) => {
              processQueue(err, null);
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
              clearUserConnectedCookie();
              router.push('/login');
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
    }

    const config = error.config;
    if (!config) return Promise.reject(error);

    config.__isRetryRequest = true;
    config.metadata = config.metadata || { retryCount: 0, totalWait: 0 };

    // Only retry for network errors or 5xx errors
    const shouldRetry =
      (!error.response || (error.response.status >= 500 && error.response.status < 600) || error.response.status == 0) &&
      config.metadata.totalWait < MAX_RETRY_TIME;

    if (shouldRetry) {
      const delay =
        config.metadata.retryCount < RETRY_DELAYS.length
          ? RETRY_DELAYS[config.metadata.retryCount]
          : RETRY_DELAYS[RETRY_DELAYS.length - 1];

      config.metadata.retryCount += 1;
      config.metadata.totalWait += delay;

      await new Promise(res => setTimeout(res, delay));
      return axios(config);
    }

    return Promise.reject(error);
  }
);

const formatDate = (date, locale, pattern = 'YYYY-MM-DD') => {
  if (locale) {
    return moment(date).locale(localStorage.getItem('currentLang')).format('LLL');
  }
  return moment(date).format(pattern)
}

const formatDateTime = (date) => {
  return moment(date).locale(localStorage.getItem('currentLang') || 'en').format('LLL');
}

const formatDateFromNow = (date) => {
  return moment(date).locale(localStorage.getItem('currentLang') || 'en').fromNow();
}

const handleError = (error) => {
  if (error.response && error.response.status && error.response.status == 401) {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    clearUserConnectedCookie()
    window.location.reload()
    return;
  }

  window.vueOpener.open({
    message: window._i18n.t(error.response && error.response.data && error.response.data.message ? error.response.data.message : 'something_went_wrong'),
    type: 'error',
    position: 'bottom',
    duration: 7000
  });

  if (error.response && error.response.data && error.response.data.message && error.response.data.message) {
    error = new Error(error.response.data.message)
  }
  error.__toastShown = true
  window.Bugsnag.notify(error)
  return error
}

const formatMoney = (number, currencyOverride = null) => {
  number = typeof number === 'number' ? number : parseFloat(number);
  if (isNaN(number)) return '';

  const currency = currencyOverride || localStorage.getItem('currentCurrency') || '$';
  const formattedNumber = number.toFixed(2);

  const currencyMap = {
    // Major currencies
    'dh': { locale: 'fr-MA', currency: 'MAD', replaceWith: 'DH' },
    'DH': { locale: 'fr-MA', currency: 'MAD', replaceWith: 'DH' },
    'MAD': { locale: 'fr-MA', currency: 'MAD', replaceWith: 'DH' },
    '$': { locale: 'en-US', currency: 'USD' },
    'USD': { locale: 'en-US', currency: 'USD' },
    '€': { locale: 'fr-FR', currency: 'EUR' },
    'EUR': { locale: 'fr-FR', currency: 'EUR' },
    '£': { locale: 'en-GB', currency: 'GBP' },
    'GBP': { locale: 'en-GB', currency: 'GBP' },
    '¥': { locale: 'ja-JP', currency: 'JPY' },
    '₹': { locale: 'en-IN', currency: 'INR' },
    '₩': { locale: 'ko-KR', currency: 'KRW' },
    '₽': { locale: 'ru-RU', currency: 'RUB' },
    '₴': { locale: 'uk-UA', currency: 'UAH' },
    '₺': { locale: 'tr-TR', currency: 'TRY' },
    'A$': { locale: 'en-AU', currency: 'AUD' },
    'C$': { locale: 'en-CA', currency: 'CAD' },
    'R$': { locale: 'pt-BR', currency: 'BRL' },
    'NZ$': { locale: 'en-NZ', currency: 'NZD' },
    'HK$': { locale: 'zh-HK', currency: 'HKD' },
    'SG$': { locale: 'en-SG', currency: 'SGD' },
    'R': { locale: 'en-ZA', currency: 'ZAR' },

    // African currencies
    'DZD': { locale: 'ar-DZ', currency: 'DZD' },
    'TND': { locale: 'ar-TN', currency: 'TND' },
    'DT': { locale: 'ar-TN', currency: 'TND', replaceWith: 'DT' },
    'EGP': { locale: 'ar-EG', currency: 'EGP', replaceWith: 'EGP' },
    'AOA': { locale: 'pt-AO', currency: 'AOA' },
    'XOF': { locale: 'fr-BJ', currency: 'XOF' },
    'BWP': { locale: 'en-BW', currency: 'BWP' },
    'CDF': { locale: 'fr-CD', currency: 'CDF' },
    'XAF': { locale: 'fr-CF', currency: 'XAF' },
    'DJF': { locale: 'fr-DJ', currency: 'DJF' },
    'ERN': { locale: 'en-ER', currency: 'ERN' },
    'ETB': { locale: 'am-ET', currency: 'ETB' },
    'GHS': { locale: 'en-GH', currency: 'GHS' },
    'GMD': { locale: 'en-GM', currency: 'GMD' },
    'GNF': { locale: 'fr-GN', currency: 'GNF' },
    'KES': { locale: 'en-KE', currency: 'KES' },
    'KMF': { locale: 'ar-KM', currency: 'KMF' },
    'LRD': { locale: 'en-LR', currency: 'LRD' },
    'LSL': { locale: 'en-LS', currency: 'LSL' },
    'LYD': { locale: 'ar-LY', currency: 'LYD' },
    'MGA': { locale: 'fr-MG', currency: 'MGA' },
    'MRU': { locale: 'ar-MR', currency: 'MRU' },
    'MWK': { locale: 'en-MW', currency: 'MWK' },
    'MUR': { locale: 'en-MU', currency: 'MUR' },
    'MZN': { locale: 'pt-MZ', currency: 'MZN' },
    'NAD': { locale: 'en-NA', currency: 'NAD' },
    'NGN': { locale: 'en-NG', currency: 'NGN', replaceWith: '₦' },
    'RWF': { locale: 'rw-RW', currency: 'RWF' },
    'SCR': { locale: 'en-SC', currency: 'SCR' },
    'SDG': { locale: 'ar-SD', currency: 'SDG' },
    'SHP': { locale: 'en-SH', currency: 'SHP' },
    'SLL': { locale: 'en-SL', currency: 'SLL' },
    'SOS': { locale: 'so-SO', currency: 'SOS' },
    'SSP': { locale: 'en-SS', currency: 'SSP' },
    'STN': { locale: 'pt-ST', currency: 'STN' },
    'SZL': { locale: 'en-SZ', currency: 'SZL' },
    'TZS': { locale: 'sw-TZ', currency: 'TZS' },
    'UGX': { locale: 'en-UG', currency: 'UGX' },
    'ZMW': { locale: 'en-ZM', currency: 'ZMW' },
    'ZWL': { locale: 'en-ZW', currency: 'ZWL' },
  };

  const config = currencyMap[currency];

  if (!config) {
    // fallback: just show the symbol with formatted number
    return `${currency} ${formattedNumber}`;
  }

  const formatter = new Intl.NumberFormat(config.locale, {
    style: 'currency',
    currency: config.currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  let formatted = formatter.format(number);

  // Replace 'MAD' with 'DH' if needed
  if (config.replaceWith) {
    if (config.currency === 'MAD' && localStorage.getItem('currentLang') === 'ar') {
      formatted = formatted.replace('MAD', 'درهم');
    } else {
      formatted = formatted.replace(config.currency, config.replaceWith);
    }
  }

  return formatted;
};

const customPost = (endpoint, data, options = {}) => {
  return new Promise((resolve, reject) => {
    axios.post(endpoint, data, {
      ...options,
      headers: {
        authorization: localStorage.getItem('access_token'),
        ...(options.headers || {})
      }
    })
      .then((response) => {
        if (options && options.showToast && options.showToast === true) {
          window.vueOpener.open({
            message: window._i18n.t('successfully_saved'),
            type: 'success',
            position: 'bottom',
          });
        }
        return resolve(response)
      })
      .catch(error => { return reject(options.silent ? error : handleError(error)) })
  })
}

const customGet = (endpoint, options = {}) => {
  return new Promise((resolve, reject) => {
    axios.get(endpoint, {
      ...options,
      headers: {
        authorization: localStorage.getItem('access_token'),
        ...(options.headers || {})
      }
    })
      .then((response) => {
        if (options && options.showToast && options.showToast === true) {
          window.vueOpener.open({
            message: window._i18n.t('successfully_saved'),
            type: 'success',
            position: 'bottom',
          });
        }
        return resolve(response)
      })
      .catch(error => { return reject(options.silent ? error : handleError(error)) })
  })
}

let deleteQueue = Promise.resolve();

const customDelete = (endpoint, options = {}) => {
  return new Promise((resolve, reject) => {
    deleteQueue = deleteQueue
      .then(async () => {
        try {
          const response = await axios.delete(endpoint, {
            ...options,
            headers: {
              authorization: localStorage.getItem('access_token'),
              ...(options.headers || {})
            }
          });
          if (options && options.showToast && options.showToast === true) {
            window.vueOpener.open({
              message: window._i18n.t('successfully_deleted'),
              type: 'success',
              position: 'bottom'
            });
          }
          resolve(response);
        } catch (error) {
          try {
            reject(handleError(error));
          } catch (e) {
            reject(e);
          }
        }
        await new Promise(r => setTimeout(r, 200));
      })
      .catch(() => { });
  });
}

// Keep table state in sync after a successful delete without fetching the page again.
// IDs are normalized to strings because some API resources return numeric IDs while
// route/component values can be strings.
const withoutDeletedRecords = (records, deletedRecords) => {
  if (!Array.isArray(records)) return records

  const deleted = Array.isArray(deletedRecords) ? deletedRecords : [deletedRecords]
  const deletedIds = new Set(
    deleted
      .map(record => record && typeof record === 'object' ? record.id : record)
      .filter(id => id !== undefined && id !== null)
      .map(id => String(id))
  )

  return records.filter(record => !record || !deletedIds.has(String(record.id)))
}

const fulfilledRecordIds = (recordIds, settledResults) => {
  return recordIds.filter((id, index) => settledResults[index] && settledResults[index].status === 'fulfilled')
}

const customPatch = (endpoint, data, options = {}) => {
  return new Promise((resolve, reject) => {
    axios.patch(endpoint, data, {
      ...options,
      headers: {
        authorization: localStorage.getItem('access_token'),
        ...(options.headers || {})
      }
    })
      .then((response) => {
        if (options && options.showToast && options.showToast === true) {
          window.vueOpener.open({
            message: window._i18n.t('successfully_updated'),
            type: 'success',
            position: 'bottom'
          });
        }
        return resolve(response)
      })
      .catch(error => { return reject(options.silent ? error : handleError(error)) })
  })
}

const maxLengthBorder = (length) => {
  if (length > 3) return 'border-bottom-color: #4CAF50'
  return 'border-bottom-color: #f44336'
}

const formatReference = (template, id) => {
  let filters;
  if (!template) return ""
  //search for fitlers
  if (template && template.length && template.includes('#')) {
    const index = template.indexOf('#')
    //get filters object
    filters = JSON.parse(template.substring(index, template.length).replace('#', ''))

    //remove object from reference
    template = template.substring(0, index)
  }

  //refrence contains id
  if (id != undefined) {
    if (filters && filters.start) {
      id += parseInt(filters.start)
    } else {
      id++
    }
    return template.replace('{{DAY}}', moment().format('DD')).replace('{{MONTH}}', moment().format('MM')).replace('{{YEAR}}', moment().format('YY')).replace('{{ID}}', id)
  }
  return template.replace('{{DAY}}', moment().format('DD')).replace('{{MONTH}}', moment().format('MM')).replace('{{YEAR}}', moment().format('YY'))
}

const CustomToFixed = (number) => {
  return number.toFixed(2)
}

const setDraft = (object, type) => {
  localStorage.setItem(type, object)
}

const permissionCandidates = (permission) => {
  const candidates = [permission]
  const actionMatch = permission.match(/^(create|edit|delete|manage)_(.+)$/)

  if (actionMatch) {
    const subject = actionMatch[2]
    candidates.push(`view_${subject}`)
    if (!subject.endsWith('s')) candidates.push(`view_${subject}s`)
  }

  return [...new Set(candidates)]
}

const moduleAllowsPlan = (planKey, moduleKey, permission) => {
  return moduleKey === 'settings' || (
    canPlanAccessModule(planKey, moduleKey) &&
    (!permission || canPlanAccessModuleItem(planKey, moduleKey, permission))
  )
}

const findModuleForPermission = (modules, permission, requireEnabled = true) => {
  const candidates = permissionCandidates(permission)
  return modules.find(mod => (
    (!requireEnabled || mod.enabled === true) &&
    (mod.items || []).some(item => candidates.includes(item.permission) && (!requireEnabled || item.enabled === true))
  ))
}

const checkAccess = (permission) => {
  if (store.getters.BUSINESS && store.getters.BUSINESS.trial_end_at) {
    const leftDays = moment(store.getters.BUSINESS.trial_end_at).diff(moment(), "days")
    if ((!store.getters.BUSINESS.status || (store.getters.BUSINESS.status != 'active') && leftDays <= 0)) return -2
  } else {
    if ((!store.getters.BUSINESS.status || store.getters.BUSINESS.status != 'active')) return -2
  }
  if ((!store.getters.BUSINESS.status || (!['active', 'trial', 'on_trial'].includes(store.getters.BUSINESS.status)))) return -2

  const planKey = getBusinessPlanKey(store.getters.BUSINESS);
  const module = findModuleForPermission(store.getters.MODULES, permission);

  if (!module) {
    const rawModule = findModuleForPermission(store.state.modules, permission, false);
    if (rawModule && !moduleAllowsPlan(planKey, rawModule.module_key, permission)) return -2;
    if (rawModule) return -1;
    if (permission.includes('view_')) return -1;
  } else if (!moduleAllowsPlan(planKey, module.module_key, permission)) {
    return -2
  }

  if (store.getters.ROLE === 'owner') return true
  if (!store.getters.PERMISSIONS) return -1
  if (!store.getters.PERMISSIONS.find(p => p == permission)) return -1

  return 1
}

const checkModuleIsEnabled = (moduleName) => {
  const module = store.getters.MODULES.find(mod => mod.enabled === true && mod.items.some(item => item.title === moduleName && item.enabled === true));
  if (!module) return false;
  const planKey = getBusinessPlanKey(store.getters.BUSINESS);
  const item = module.items.find(item => item.title === moduleName && item.enabled === true);
  if (!canPlanAccessModuleItem(planKey, module.module_key, item?.permission, item?.title)) return false;
  return true
}

const formatProducts = (rows, preserveIds = false) => {
  const products = []
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].custom_product == undefined) {
      rows[i].custom_product = false
    }
    products.push({
      ...(preserveIds ? { id: rows[i].id || null } : {}),
      product_label: rows[i].product_label || rows[i].label,
      price: rows[i].price,
      purchase_price: rows[i].custom_product === false ? rows[i].purchase_price : -1,
      quantity: rows[i].quantity,
      total: rows[i].total,
      sub_total: rows[i].sub_total,
      vat: rows[i].vat,
      unity: rows[i].unity,
      discount: rows[i].discount ? parseFloat(rows[i].discount) : 0,
      product_id: rows[i].custom_product === false ? rows[i].product_id || rows[i].id : "-1",
      inventory_id: rows[i].custom_product === false ? rows[i].inventory_id : "-1",
      custom_product: rows[i].custom_product
    })
  }
  return products
}

// Product descriptions created with Quill are inserted into invoice/quote
// template HTML. Keep only the small formatting subset supported by the
// product editor before that content reaches an innerHTML rendering path.
const sanitizeRichText = (value) => {
  const html = String(value || '')
  if (!html) return ''

  if (typeof document === 'undefined') {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  const container = document.createElement('div')
  container.innerHTML = html

  const allowedTags = new Set([
    'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'SPAN',
    'DIV', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'SUB', 'SUP'
  ])
  const blockedTags = new Set([
    'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH',
    'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'OPTION', 'LINK',
    'META', 'BASE'
  ])
  const safeColor = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|transparent|currentcolor)$/i

  Array.from(container.querySelectorAll('*')).forEach(element => {
    if (blockedTags.has(element.tagName)) {
      element.remove()
      return
    }

    if (!allowedTags.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes))
      return
    }

    const classes = Array.from(element.classList).filter(className => (
      /^ql-align-(center|right|justify)$/.test(className)
      || className === 'ql-direction-rtl'
    ))
    const safeStyles = []
    if (classes.includes('ql-align-center')) safeStyles.push('text-align: center')
    if (classes.includes('ql-align-right')) safeStyles.push('text-align: right')
    if (classes.includes('ql-align-justify')) safeStyles.push('text-align: justify')
    if (classes.includes('ql-direction-rtl')) safeStyles.push('direction: rtl')
    if (['P', 'DIV', 'BLOCKQUOTE'].includes(element.tagName)) safeStyles.push('margin: 0')
    if (['UL', 'OL'].includes(element.tagName)) safeStyles.push('margin: 0', 'padding-left: 1.25rem')
    ;['color', 'background-color'].forEach(property => {
      const propertyValue = element.style.getPropertyValue(property).trim()
      if (propertyValue && safeColor.test(propertyValue)) {
        safeStyles.push(`${property}: ${propertyValue}`)
      }
    })

    Array.from(element.attributes).forEach(attribute => {
      element.removeAttribute(attribute.name)
    })
    if (classes.length) element.setAttribute('class', classes.join(' '))
    if (safeStyles.length) element.setAttribute('style', safeStyles.join('; '))
  })

  return container.innerHTML
}

const richTextToPlainText = (value) => {
  const sanitizedHtml = sanitizeRichText(value)
  if (typeof document === 'undefined') {
    return sanitizedHtml.replace(/<[^>]*>/g, ' ')
  }

  const container = document.createElement('div')
  container.innerHTML = sanitizedHtml
  return container.textContent || ''
}

const createCatalogProductsFromCustomRows = async (rows = []) => {
  const selectedRows = rows.filter(row => row.custom_product === true && row.create_catalog_product === true)

  for (const row of selectedRows) {
    const label = richTextToPlainText(row.product_label || row.label)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 255)

    await customPost(`${process.env.VUE_APP_API}/products`, {
      label,
      price: Number(row.price) || 0,
      purchase_price: 0,
      quantity: 0,
      min_quantity: 0,
      unity: row.unity || null,
      price_includes_tax: row.price_includes_tax == null
        ? store.getters.BUSINESS?.price_includes_tax === true
        : row.price_includes_tax === true,
      has_variants: false,
      variants: []
    }, { showToast: false })

    // Prevent a later retry of the document save from creating a duplicate.
    row.create_catalog_product = false
  }
}

const generateRandString = (length, only_lowercase) => {
  let text = '';

  const possible = only_lowercase && only_lowercase === true ? 'abcdefghijklmnopqrstuvwxyz0123456789' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < length; i++) text += possible.charAt(Math.floor(Math.random() * possible.length));

  return text;
};

const jsonToHtml = (jsonTree) => {
  // List of self-closing tags
  const selfClosingTags = ['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr'];

  // Base case: if jsonNode is a text node, return its value
  if (typeof jsonTree === 'string') {
    return jsonTree;
  }

  // Start building HTML string
  let htmlString = `<${jsonTree.tag}`;

  // Add attributes
  if (jsonTree.attributes) {
    for (const [key, value] of Object.entries(jsonTree.attributes)) {
      htmlString += ` ${key}="${value}"`;
    }
  }

  // Close start tag
  htmlString += selfClosingTags.includes(jsonTree.tag.toLowerCase()) ? ' />' : '>';

  // Add children recursively if not a self-closing tag
  if (!selfClosingTags.includes(jsonTree.tag.toLowerCase()) && jsonTree.children) {
    for (const child of jsonTree.children) {
      htmlString += jsonToHtml(child);
    }

    // Close tag
    htmlString += `</${jsonTree.tag}>`;
  }

  return htmlString;
}

// import { 
//   ref,
//   uploadBytes,
//   getStorage,
//   getDownloadURL 
// } from "firebase/storage";
// import {app} from './services/firebase'

// const storage = getStorage(app)

// const customUploadFile = (file, path) => {
//   return new Promise((resolve, reject) => {
//     if (file.size > 5 * 1024 * 1024) {
//       return reject(new Error('File size exceeds 5MB'));
//     }

//     const storageRef = ref(storage, path);

//     uploadBytes(storageRef, file)
//       .then(() => getDownloadURL(storageRef))
//       .then((url) => {
//         resolve(url);
//       })
//       .catch((error) => {
//         reject(error);
//       });
//   });
// };

const customUploadFile = (file, path, entityMeta = {}) => {
  return new Promise((resolve, reject) => {
    if (file.size > 20 * 1024 * 1024) {
      return reject(new Error('File size exceeds 20MB'));
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path)
    // Add optional metadata
    if (entityMeta.entity_type) formData.append('entity_type', entityMeta.entity_type);
    if (entityMeta.entity_id) formData.append('entity_id', entityMeta.entity_id);
    if (entityMeta.folder_id) formData.append('folder_id', entityMeta.folder_id);
    if (entityMeta.is_private != null) formData.append('is_private', entityMeta.is_private);
    if (entityMeta.allowed_users) formData.append('allowed_users', JSON.stringify(entityMeta.allowed_users));
    if (entityMeta.notes) formData.append('notes', entityMeta.notes);
    if (entityMeta.tags) formData.append('tags', JSON.stringify(entityMeta.tags));
    if (entityMeta.products || entityMeta.public) formData.append('products', true)
    axios.post(`${process.env.VUE_APP_API}/attachments/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then((response) => {
        return resolve(response.data.url)
      })
      .catch(error => {
        reject(handleError(error));
      });
  });
};

const parsePriceHelper = (priceStr) => {
  if (typeof priceStr === 'number') return priceStr;
  if (!priceStr) return 0;
  let clean = priceStr.replace(/[^0-9.]/g, '');
  return parseFloat(clean) || 0;
}

const processScannedEntities = async (data, context = 'client') => {
  // ... (original content)
  // context: 'client' (for client Invoice) or 'supplier' (for supplier Invoice)

  // 1. Client/Supplier Auto-Creation
  if (data.supplier_name) {
    const name = data.supplier_name;
    try {
      if (context === 'client') {
        // Check Client
        const response = await customGet(`${process.env.VUE_APP_API}/clients?keyword=${encodeURIComponent(name)}`);
        if (response.data && response.data.data && response.data.data.length > 0) {
          data.existing_client = response.data.data[0];
        } else {
          // Create Client
          const newClient = { name: name, phone: '0000000000', notes: 'Auto-created from scan invoice.' };
          const createResp = await customPost(`${process.env.VUE_APP_API}/clients`, newClient);
          data.existing_client = createResp.data.client || createResp.data;
          if (!data.notes) data.notes = '';
          data.notes += `\nAuto-created client: ${name}`;
        }
      } else {
        // Supplier is intentionally not created or attached from scanned invoices.
        data.notes = `${data.notes || ''}\nExtracted Name: ${name}`.trim();
      }
    } catch (e) {
      alert(e)
    }
  }

  // 2. Products
  if (data.products && Array.isArray(data.products)) {
    const processedProducts = [];
    for (const p of data.products) {
      const label = p.label || p.name;
      if (!label) continue;
      try {

        if (context === 'client') {
          const response = await customGet(`${process.env.VUE_APP_API}/inventories?keyword=${encodeURIComponent(label)}`);
          if (response.data && response.data.data && response.data.data.length > 0) {
            const inv = response.data.data[0];
            // Map inventory data
            p.inventory_id = inv.id;
            p.product_id = inv.product ? inv.product.id : inv.product_id;
            p.id = p.product_id; // For fallback
            p.label = inv.product ? inv.product.label : label;
            p.vat = inv.product ? inv.product.vat : (inv.vat || 0);

            const scannedPrice = parsePriceHelper(p.price);
            // Use scanned price if valid, else use system price
            p.price = scannedPrice > 0 ? scannedPrice : (inv.product ? inv.product.price : 0);
          } else {
            // Fallback: Create Product (and Inventory?)
            const newProduct = {
              label: label,
              price: parsePriceHelper(p.price),
              purchase_price: 0,
              vat: 0,
              quantity: p.quantity || 0,
              notes: 'Auto-created from scan invoice',
              has_variants: false,
              variants: []
            };
            const createResp = await customPost(`${process.env.VUE_APP_API}/products`, newProduct);
            const created = createResp.data.product || createResp.data;
            p.product_id = created.id;
            p.id = created.id;
            p.vat = created.vat;
          }
        } else {
          // Supplier Context - Search Products
          const response = await customGet(`${process.env.VUE_APP_API}/products?keyword=${encodeURIComponent(label)}`);
          if (response.data && response.data.data && response.data.data.length > 0) {
            const existing = response.data.data[0];
            p.product_id = existing.id;
            p.id = existing.id;
            p.vat = existing.vat;
            const scannedPrice = parsePriceHelper(p.price);
            p.price = scannedPrice > 0 ? scannedPrice : existing.price;
          } else {
            // Create
            const newProduct = {
              label: label,
              price: parsePriceHelper(p.price),
              purchase_price: 0,
              vat: 0,
              quantity: p.quantity || 0,
              notes: 'Auto-created from scan invoice',
              has_variants: false,
              variants: []
            };
            const createResp = await customPost(`${process.env.VUE_APP_API}/products`, newProduct);
            const created = createResp.data.product || createResp.data;
            p.product_id = created.id;
            p.id = created.id;
            p.vat = created.vat;
          }
        }
      } catch (e) {
        alert(e)
      }
      processedProducts.push(p);
    }
    data.products = processedProducts;
  }
  return data;
}

const analyzeScannedEntities = async (data, context = 'client') => {
  // Return structure holding analysis state without mutating data
  const analysis = {
    contact: {
      name: data.supplier_name || 'Unknown',
      exists: false,
      existingEntity: null,
      shouldCreate: true // default action for mapping UI
    },
    products: []
  };

  // 1. Check Contact
  if (data.supplier_name) {
    const name = data.supplier_name;
    try {
      if (context === 'client') {
        const response = await customGet(`${process.env.VUE_APP_API}/clients?keyword=${encodeURIComponent(name)}`);
        if (response.data && response.data.data && response.data.data.length > 0) {
          analysis.contact.exists = true;
          analysis.contact.existingEntity = response.data.data[0];
          analysis.contact.shouldCreate = false; // Already exists
        }
      } else {
        const response = await customGet(`${process.env.VUE_APP_API}/suppliers?keyword=${encodeURIComponent(name)}`);
        if (response.data && response.data.data && response.data.data.length > 0) {
          analysis.contact.exists = true;
          analysis.contact.existingEntity = response.data.data[0];
          analysis.contact.shouldCreate = false;
        }
      }
    } catch (e) {
      // Suppress errors during dry-run matching
    }
  }

  // 2. Check Products
  if (data.products && Array.isArray(data.products)) {
    for (const p of data.products) {
      const label = p.label || p.name;
      if (!label) continue;

      const pAnalysis = {
        originalData: p,
        label: label,
        exists: false,
        existingEntity: null,
        shouldCreate: true, // true: Add to stock, false: Custom Product (one-time)
        scannedPrice: parsePriceHelper(p.price),
        scannedQuantity: p.quantity || 1
      };

      try {
        if (context === 'client') {
          const response = await customGet(`${process.env.VUE_APP_API}/inventories?keyword=${encodeURIComponent(label)}`);
          if (response.data && response.data.data && response.data.data.length > 0) {
            pAnalysis.exists = true;
            pAnalysis.existingEntity = response.data.data[0];
            pAnalysis.shouldCreate = false;
          }
        } else {
          const response = await customGet(`${process.env.VUE_APP_API}/products?keyword=${encodeURIComponent(label)}`);
          if (response.data && response.data.data && response.data.data.length > 0) {
            pAnalysis.exists = true;
            pAnalysis.existingEntity = response.data.data[0];
            pAnalysis.shouldCreate = false;
          }
        }
      } catch (e) {
        // Suppress errors during dry-run matching
      }

      analysis.products.push(pAnalysis);
    }
  }
  return analysis;
}


export default {
  formatDate,
  formatDateTime,
  formatDateFromNow,
  handleError,
  formatMoney,
  customPost,
  customGet,
  customDelete,
  withoutDeletedRecords,
  fulfilledRecordIds,
  customPatch,
  maxLengthBorder,
  formatReference,
  CustomToFixed,
  setDraft,
  checkAccess,
  formatProducts,
  sanitizeRichText,
  richTextToPlainText,
  createCatalogProductsFromCustomRows,
  generateRandString,
  jsonToHtml,
  customUploadFile,
  checkModuleIsEnabled,
  shortId,
  setUserConnectedCookie,
  clearUserConnectedCookie,
  processScannedEntities,
  analyzeScannedEntities
}
